import React, { useState } from 'react';
import styled from 'styled-components';
import { apiRequest } from '../../services/apiService';

const PageContainer = styled.div`
  max-width: 720px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-lg);
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
`;

const Title = styled.h1`
  font-size: var(--font-size-3xl);
  font-family: var(--font-serif);
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary);
  margin: 0;
`;

const Subtitle = styled.p`
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
  padding: var(--space-lg);
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
`;

const Label = styled.label`
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
`;

const Input = styled.input`
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  padding: var(--space-sm);
  font-size: var(--font-size-md);
  min-height: 48px;
  
  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 4px rgba(0, 24, 206, 0.08);
  }
`;

const TemplateToggle = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-xs);
  padding: 4px;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  background: var(--color-background-secondary);
`;

const TemplateOption = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: var(--space-xs) var(--space-sm);
  border-radius: var(--border-radius-sm);
  color: ${({ $checked }) => ($checked ? 'white' : 'var(--color-text-secondary)')};
  background: ${({ $checked }) => ($checked ? 'var(--color-accent)' : 'transparent')};
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  text-align: center;
  cursor: pointer;
`;

const TemplateRadio = styled.input`
  position: absolute;
  opacity: 0;
`;

const EmailPreview = styled.section`
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  padding: var(--space-md);
  border: 1px solid var(--color-border-light);
  border-radius: var(--border-radius-md);
  background: rgba(247, 247, 243, 0.68);
`;

const PreviewHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PreviewLabel = styled.p`
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const PreviewSubject = styled.p`
  margin: 0;
  color: var(--color-primary);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-medium);
`;

const PreviewBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.6;

  p {
    margin: 0;
  }
`;

const PreviewLink = styled.span`
  color: var(--color-accent);
  font-weight: var(--font-weight-medium);
`;

const SubmitButton = styled.button`
  align-self: flex-start;
  background: var(--color-periwinkle);
  color: white;
  border: none;
  border-radius: var(--border-radius-md);
  padding: var(--space-sm) var(--space-lg);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  
  &:hover {
    background: var(--color-accent);
  }

  &:disabled {
    background: var(--color-text-secondary);
    cursor: not-allowed;
  }
`;

const InfoCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  padding: var(--space-lg);
  background: rgba(255, 255, 255, 0.68);
  border-radius: var(--border-radius-md);
  border: 1px dashed var(--color-border);
`;

const InfoTitle = styled.h2`
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
  color: var(--color-primary);
  margin: 0;
`;

const InfoList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
`;

const InfoItem = styled.li`
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
`;

const SuccessMessage = styled.div`
  padding: var(--space-md);
  border-radius: var(--border-radius-md);
  background: rgba(130, 172, 216, 0.14);
  border: 1px solid rgba(130, 172, 216, 0.36);
  color: var(--color-accent);
  font-weight: var(--font-weight-medium);
`;

const ErrorMessage = styled.div`
  padding: var(--space-md);
  border-radius: var(--border-radius-md);
  background: rgba(180, 35, 24, 0.08);
  border: 1px solid rgba(180, 35, 24, 0.22);
  color: var(--color-error);
  font-weight: var(--font-weight-medium);
`;

const getEmailPreview = (template, artistName) => {
  const name = artistName.trim() || 'Artist Name';

  if (template === 'personalised') {
    return {
      subject: "Your Collector's Eye Studio invitation",
      paragraphs: [
        `Hello ${name},`,
        "You have been personally invited to join Collector's Eye Studio.",
        'If you have any questions, please send them to jpower@thecollectorseye.com.',
      ],
      links: ['Set up your artist account', 'jpower@thecollectorseye.com'],
    };
  }

  return {
    subject: "Your Collector's Eye Studio invitation",
    paragraphs: [
      `Hello ${name},`,
      "You have been invited to join Collector's Eye Studio.",
      'If you have any questions, please send them to jpower@thecollectorseye.com.',
    ],
    links: ['Set up your artist account', 'jpower@thecollectorseye.com'],
  };
};

const InviteArtist = () => {
  const defaultInvite = {
    name: '',
    email: '',
    emailTemplate: 'standard',
  };

  const [formState, setFormState] = useState(defaultInvite);
  const [status, setStatus] = useState('idle'); // idle, submitting, success, error
  const [errorMsg, setErrorMsg] = useState('');
  const emailPreview = getEmailPreview(formState.emailTemplate, formState.name);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
    if (status !== 'idle') {
      setStatus('idle');
      setErrorMsg('');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus('submitting');
    setErrorMsg('');

    try {
      await apiRequest('/admin/artists/invites', {
        method: 'POST',
        body: {
          name: formState.name,
          email: formState.email,
          emailTemplate: formState.emailTemplate,
        },
      });

      setStatus('success');
      setFormState(defaultInvite);
    } catch (err) {
      console.error('Error sending invite:', err);
      setStatus('error');
      // Extract error message from Cloud Function error if available
      const message = err.message || 'Failed to send invite. Please check your connection and permissions.';
      setErrorMsg(message);
    }
  };

  return (
    <PageContainer>
      <PageHeader>
        <Title>Invite an Artist</Title>
        <Subtitle>
          Create an account for an artist and send them a welcome email to set their password.
        </Subtitle>
      </PageHeader>

      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="name">Artist name</Label>
          <Input
            id="name"
            name="name"
            value={formState.name}
            onChange={handleChange}
            placeholder="Artist full name"
            required
            disabled={status === 'submitting'}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="email">Artist email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formState.email}
            onChange={handleChange}
            placeholder="name@example.com"
            required
            disabled={status === 'submitting'}
          />
        </FormGroup>
        <FormGroup>
          <Label id="email-template-label">Invitation email template</Label>
          <TemplateToggle role="radiogroup" aria-labelledby="email-template-label">
            <TemplateOption $checked={formState.emailTemplate === 'standard'}>
              <TemplateRadio
                type="radio"
                name="emailTemplate"
                value="standard"
                checked={formState.emailTemplate === 'standard'}
                onChange={handleChange}
                disabled={status === 'submitting'}
              />
              Standard invite
            </TemplateOption>
            <TemplateOption $checked={formState.emailTemplate === 'personalised'}>
              <TemplateRadio
                type="radio"
                name="emailTemplate"
                value="personalised"
                checked={formState.emailTemplate === 'personalised'}
                onChange={handleChange}
                disabled={status === 'submitting'}
              />
              Personalised outreach
            </TemplateOption>
          </TemplateToggle>
        </FormGroup>
        <EmailPreview aria-label="Email preview">
          <PreviewHeader>
            <PreviewLabel>Email preview</PreviewLabel>
            <PreviewSubject>{emailPreview.subject}</PreviewSubject>
          </PreviewHeader>
          <PreviewBody>
            {emailPreview.paragraphs.map((paragraph) => (
              <p key={paragraph}>
                {paragraph.split('\n').map((line, index) => (
                  <React.Fragment key={line}>
                    {index > 0 && <br />}
                    {line}
                  </React.Fragment>
                ))}
              </p>
            ))}
            <p>
              {emailPreview.links.map((link, index) => (
                <React.Fragment key={link}>
                  {index > 0 && ' · '}
                  <PreviewLink>{link}</PreviewLink>
                </React.Fragment>
              ))}
            </p>
          </PreviewBody>
        </EmailPreview>
        <SubmitButton type="submit" disabled={status === 'submitting'}>
          {status === 'submitting' ? 'Processing...' : 'Create Account & Send Email'}
        </SubmitButton>
        {status === 'success' && (
          <SuccessMessage role="status">
            Account created and invitation email sent successfully!
          </SuccessMessage>
        )}
        {status === 'error' && (
          <ErrorMessage role="alert">
            {errorMsg}
          </ErrorMessage>
        )}
      </Form>

      <InfoCard>
        <InfoTitle>How this works</InfoTitle>
        <InfoList>
          <InfoItem>An account is immediately created for the artist.</InfoItem>
          <InfoItem>They receive an email to set their password.</InfoItem>
          <InfoItem>Once they set a password, they can log in immediately.</InfoItem>
        </InfoList>
      </InfoCard>
    </PageContainer>
  );
};

export default InviteArtist;
